import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, User, Shield, UserCog } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter
} from '../../components/ui/dialog';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '../../components/ui/select';
import { 
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import api from '../../lib/api';
import { formatDate } from '../../lib/utils';
import { useAuth } from '../../context/AuthContext';

const roleColors = {
  admin: 'bg-red-500/20 text-red-400',
  agent: 'bg-blue-500/20 text-blue-400',
  reception: 'bg-purple-500/20 text-purple-400',
  client: 'bg-gray-500/20 text-gray-400'
};

const roleLabels = {
  admin: 'Administrateur',
  agent: 'Agent Interne',
  reception: 'Réception',
  client: 'Client'
};

export const UsersManagementPage = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    role: 'agent'
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/admin/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      full_name: '',
      role: 'agent'
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      await api.post('/admin/users', formData);
      toast.success('Utilisateur créé');
      setShowForm(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      console.error('Error creating user:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    
    try {
      await api.delete(`/admin/users/${deleteConfirm}`);
      toast.success('Utilisateur supprimé');
      setDeleteConfirm(null);
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-sky-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="users-management-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Gestion des Utilisateurs</h2>
          <p className="text-gray-400">{users.length} utilisateurs</p>
        </div>
        <Button 
          className="bg-sky-500 hover:bg-sky-600"
          onClick={() => setShowForm(true)}
          data-testid="create-user-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nouvel Utilisateur
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {Object.entries(roleLabels).map(([role, label]) => {
          const count = users.filter(u => u.role === role).length;
          const Icon = role === 'admin' ? Shield : role === 'agent' ? UserCog : User;
          return (
            <Card key={role} className="bg-[#0F172A] border-white/10">
              <CardContent className="p-4 flex items-center gap-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${roleColors[role].replace('text-', 'bg-').replace('/20', '/10')}`}>
                  <Icon className={`w-6 h-6 ${roleColors[role].split(' ')[1]}`} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{count}</p>
                  <p className="text-sm text-gray-400">{label}s</p>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Users List */}
      <Card className="bg-[#0F172A] border-white/10">
        <CardContent className="p-0">
          <div className="divide-y divide-white/10">
            {users.map((user) => (
              <div 
                key={user.id}
                className="p-4 flex items-center justify-between"
                data-testid={`user-row-${user.id}`}
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-sky-500/20 flex items-center justify-center">
                    <span className="text-lg font-semibold text-sky-400">
                      {user.full_name?.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium text-white">{user.full_name}</p>
                    <p className="text-sm text-gray-400">{user.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <Badge className={roleColors[user.role]}>
                    {roleLabels[user.role]}
                  </Badge>
                  <span className="text-sm text-gray-500 hidden md:block">
                    {formatDate(user.created_at)}
                  </span>
                  {user.id !== currentUser?.id && (
                    <Button 
                      variant="ghost" 
                      size="sm"
                      className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      onClick={() => setDeleteConfirm(user.id)}
                      data-testid={`delete-user-${user.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Create Form Dialog */}
      <Dialog open={showForm} onOpenChange={(open) => { if (!open) resetForm(); setShowForm(open); }}>
        <DialogContent className="max-w-md bg-[#0F172A] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle>Nouvel Utilisateur</DialogTitle>
            <DialogDescription className="text-gray-400">
              Créer un compte agent ou administrateur
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="user-form">
            <div>
              <Label>Nom Complet *</Label>
              <Input
                value={formData.full_name}
                onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                required
                className="bg-white/5 border-white/10 text-white"
                data-testid="user-name"
              />
            </div>

            <div>
              <Label>Email *</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                required
                className="bg-white/5 border-white/10 text-white"
                data-testid="user-email"
              />
            </div>

            <div>
              <Label>Mot de passe *</Label>
              <Input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                required
                minLength={6}
                className="bg-white/5 border-white/10 text-white"
                data-testid="user-password"
              />
            </div>

            <div>
              <Label>Rôle *</Label>
              <Select 
                value={formData.role} 
                onValueChange={(value) => setFormData({...formData, role: value})}
              >
                <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="user-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1E293B] border-white/10">
                  <SelectItem value="agent">Agent Interne</SelectItem>
                  <SelectItem value="reception">Réception</SelectItem>
                  <SelectItem value="admin">Administrateur</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                {formData.role === 'agent' && 'Traite les dossiers visa assignés'}
                {formData.role === 'reception' && 'Accueil clients, création dossiers, gestion RDV'}
                {formData.role === 'admin' && 'Accès complet à toutes les fonctionnalités'}
              </p>
            </div>

            <DialogFooter>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => { resetForm(); setShowForm(false); }}
                className="border-white/10 text-gray-300"
              >
                Annuler
              </Button>
              <Button 
                type="submit" 
                className="bg-sky-500 hover:bg-sky-600"
                data-testid="save-user-btn"
              >
                Créer
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent className="bg-[#0F172A] border-white/10 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cet utilisateur?</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-white/10 text-gray-300">Annuler</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-red-500 hover:bg-red-600"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default UsersManagementPage;
